import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, TextField, MenuItem, InputAdornment, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Snackbar, Alert, Chip, Badge,
    Autocomplete, ToggleButton, ToggleButtonGroup, CircularProgress,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import NotificationAddRoundedIcon from '@mui/icons-material/NotificationAddRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import { useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { selectEmployees } from '../../redux/slices/employeesSlice';
import ExpenseDetail from './ExpenseDetail';
import ExpenseProfile from './ExpenseProfile';
import PaymentDialog from './PaymentDialog';
import AttStatCard from '../LeaveAttendanceComps/AttStatCard';
import {
    DueReminders, ReminderDialog, localToday,
    DIALOG_PAPER, dialogTitleSx, dialogBodySx, dialogActionsSx,
} from './ExpenseReminders';
import { PRIMARY } from '../../theme';
import { card, solidBtn, tonalBtn, ghostBtn, field, th, td, EmptyState } from '../uiKit';
import SearchSelect from '../SearchSelect';
import { useCrumbActionsSetter } from '../../utils/crumbActions';
import { inr, fmtDate } from '../../utils/format';
import { PAYMENT_MODES } from './payTiming';

const OTHERS = '__others__';

const empName = (e) => `${e.firstName || ''} ${e.lastName || ''}`.trim();

/**
 * Shared list + add form behind each Expenses tab. A tab supplies its own categories and any
 * extra fields via `config`, so the three expense types stay separate screens sharing one layout.
 * `data` is the tab's state from useExpenseData.
 *
 * config: {
 *   noun, categories, extraFields: [{ key, label, required? }],
 *   trackStatus?  adds a Paid/Partially/Unpaid status (and drops Paid via),
 *   reminders?    adds reminders,
 *   shootTypes?   splits entries into these sections (e.g. Indoor / Outdoor / Local),
 *   employees?    lets an entry be tagged with employees,
 *   payTiming?    tags entries Prepaid/Postpaid and shows a summary table on top.
 * }
 *
 * Search, the category filter and the action buttons are published to the breadcrumb row.
 */
export default function ExpenseBoard({ config, data }) {
    const {
        noun, extraFields = [], trackStatus = false, reminders: hasReminders = false,
        shootTypes = null, employees: withEmployees = false, payTiming = false,
    } = config;
    const { rows, reminders = [], activeReminders = [], categories, setCategories, categoryIds = {}, summary, payments = [], loading = false, error = '' } = data;
    const employeeList = useSelector(selectEmployees);
    // Active employees as { code, name } - the API takes employee codes.
    const employeeOptions = useMemo(() => (withEmployees
        ? employeeList
            .filter((e) => e.employeeId && String(e.status || 'Active').toLowerCase() === 'active')
            .map((e) => ({ code: e.employeeId, name: e.fullName || empName(e) }))
        : []), [withEmployees, employeeList]);

    // Clicking a category (any tab) or an employee (Shoot) opens its detail view, kept in the URL so
    // Back works and switching tabs closes it.
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const entry = rows.find((r) => String(r.id) === params.get('entry')) || null;
    const detail = entry ? { kind: 'entry' } : params.get('employee') ? { kind: 'employee', value: params.get('employee') }
        : params.get('category') ? { kind: 'category', value: params.get('category') } : null;
    const openDetail = (kind, value) => setParams((p) => { const n = new URLSearchParams(p); n.set(kind, value); return n; });
    const openEntry = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set('entry', id); return n; });
    const closeDetail = () => setParams((p) => {
        const n = new URLSearchParams(p);
        ['category', 'employee', 'entry'].forEach((k) => n.delete(k));
        return n;
    });
    // From an entry, Back returns to wherever it was opened from (list or a category/employee page).
    const goBack = () => ((entry || detail?.kind === 'employee') && window.history.state?.idx > 0 ? navigate(-1) : closeDetail());

    const [customCat, setCustomCat] = useState('');
    const [editingCat, setEditingCat] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editError, setEditError] = useState(false);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [remOpen, setRemOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [customKeys, setCustomKeys] = useState({}); // extra fields currently typed instead of picked
    const [snack, setSnack] = useState(null);
    const [saving, setSaving] = useState(false);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const blank = useMemo(() => ({
        date: localToday(), category: categories[0] ?? OTHERS, amount: '', paymentMode: PAYMENT_MODES[0], notes: '', status: 'Unpaid',
        shootType: shootTypes ? shootTypes[0] : '', empRows: [{ code: '', amount: '' }],
        ...Object.fromEntries(extraFields.map((f) => [f.key, ''])),
    }), [categories, extraFields, shootTypes]);
    const [form, setForm] = useState(blank);

    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => (
            (catFilter === 'all' || r.category === catFilter)
            && (typeFilter === 'all' || r.shootType === typeFilter)
            && (!q || [r.category, r.notes, r.shootType, ...(r.employees || []), ...extraFields.map((f) => r[f.key])]
                .some((v) => String(v || '').toLowerCase().includes(q)))
        ));
    }, [rows, search, catFilter, typeFilter, extraFields]);

    const month = localToday().slice(0, 7);
    const total = summary?.total ?? rows.reduce((s, r) => s + r.amount, 0);
    const monthTotal = summary?.thisMonth ?? rows.filter((r) => r.date.startsWith(month)).reduce((s, r) => s + r.amount, 0);
    const todayTotal = summary?.today ?? rows.filter((r) => r.date === localToday()).reduce((s, r) => s + r.amount, 0);
    const dueCount = summary?.remindersSet ?? activeReminders.length;

    // Adds a typed category (or reuses a case-insensitive match) and returns its name.
    const registerCategory = (raw) => {
        const name = raw.trim();
        if (!name) return null;
        const existing = categories.find((c) => c.toLowerCase() === name.toLowerCase());
        if (!existing) setCategories((p) => [...p, name]);
        return existing || name;
    };
    const addCustomCategory = () => {
        const name = registerCategory(customCat);
        if (!name) return;
        setForm((p) => ({ ...p, category: name }));
        setCustomCat('');
    };

    // Renames a category on the server; the server cascades it onto every entry using it.
    const renameCategory = async () => {
        const name = editValue.trim();
        const clash = categories.some((c) => c !== editingCat && c.toLowerCase() === name.toLowerCase());
        if (!name || clash) { setEditError(true); return; }
        const from = editingCat;
        const id = categoryIds[from];
        const swap = (c) => (c === from ? name : c);
        try {
            if (id != null) await data.renameCategory(id, name);
            else setCategories((p) => p.map(swap)); // typed this session, not saved yet
            setForm((p) => ({ ...p, category: swap(p.category) }));
            setCatFilter(swap);
            setEditingCat(null);
            notify('Category renamed');
        } catch (err) {
            setEditError(true);
            notify(err.message, 'error');
        }
    };

    const missingExtra = extraFields.some((f) => f.required && !form[f.key].trim());
    // On a tab with employees (Shoot), each person is added with a + button as their own row: an employee picked from a dropdown and
    // their own amount. The total is the sum of the rows, and at least one person is needed.
    const empRows = form.empRows || [];
    const filledRows = empRows.filter((r) => r.code);
    const perPerson = withEmployees && filledRows.length > 0;
    const rowsOk = filledRows.length > 0
        && empRows.every((r) => (!r.code && !String(r.amount).trim()) || (r.code && Number(r.amount) > 0))
        && new Set(filledRows.map((r) => r.code)).size === filledRows.length;
    const amountsOk = withEmployees ? rowsOk : Number(form.amount) > 0;
    const canSave = form.category !== OTHERS && amountsOk && form.date && !missingExtra;

    const openAdd = () => {
        setForm({ ...blank, shootType: shootTypes && typeFilter !== 'all' ? typeFilter : blank.shootType });
        setCustomCat('');
        setEditingCat(null);
        setCustomKeys({});
        setOpen(true);
    };
    const save = async () => {
        setSaving(true);
        try {
            if (perPerson) {
                // One expense; the server takes each person's own amount and adds them up for the total.
                await data.addExpense({ ...form, employees: filledRows.map((r) => ({ employeeCode: r.code, amount: Number(r.amount) })) });
                setOpen(false);
                notify(`${noun} expense added for ${filledRows.length} ${filledRows.length === 1 ? 'person' : 'people'}`);
            } else {
                await data.addExpense({ ...form, amount: Number(form.amount) });
                setOpen(false);
                notify(`${noun} expense added`);
            }
        } catch (err) {
            notify(err.message, 'error');
        } finally {
            setSaving(false);
        }
    };
    const toggleStatus = async (r) => {
        try { await data.updateExpense(r.id, { ...r, status: r.status === 'Paid' ? 'Unpaid' : 'Paid' }); } catch (err) { notify(err.message, 'error'); }
    };
    const remove = async (id) => {
        try { await data.removeExpense(id); notify('Expense removed'); return true; } catch (err) { notify(err.message, 'error'); return false; }
    };
    const dismissReminder = async (id) => {
        try { await data.removeReminder(id); } catch (err) { notify(err.message, 'error'); }
    };

    // Search, filter and the action buttons sit at the right end of the breadcrumb row. Handlers go
    // through a ref so the buttons don't need re-publishing when they change.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { openAdd, openReminders: () => setRemOpen(true), openPayment: () => setPayOpen(true), closeDetail: goBack };
    const inDetail = !!detail;
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        if (inDetail) {
            setCrumbActions(
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => actionsRef.current.closeDetail()} sx={{ ...ghostBtn, height: 42, px: 2 }}>
                    {entry ? 'Back' : `Back to ${noun} Expenses`}
                </Button>,
            );
            return () => setCrumbActions(null);
        }
        const tall = { ...field['& .MuiOutlinedInput-root'], height: 42 };
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    size="small" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
                    sx={{ ...field, width: { xs: '100%', sm: 320 }, flexGrow: { sm: 0 }, '& .MuiOutlinedInput-root': tall }}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment> } }}
                />
                <Box sx={{ width: 190 }}>
                    <SearchSelect
                        value={catFilter} onChange={(e) => setCatFilter(e.target.value)} searchPlaceholder="Search categories…"
                        sx={{ ...field, '& .MuiOutlinedInput-root': tall }}
                        options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
                    />
                </Box>
                {hasReminders && (
                    <Badge badgeContent={dueCount} color="error" overlap="rectangular">
                        <Button startIcon={<NotificationAddRoundedIcon />} onClick={() => actionsRef.current.openReminders()} sx={{ ...tonalBtn, height: 42, px: 2 }}>
                            Set Reminder
                        </Button>
                    </Badge>
                )}
                {payTiming && (
                    <Button startIcon={<PaymentsRoundedIcon />} onClick={() => actionsRef.current.openPayment()} sx={{ ...tonalBtn, height: 42, px: 2 }}>
                        Add Payment
                    </Button>
                )}
                <Button startIcon={<AddRoundedIcon />} onClick={() => actionsRef.current.openAdd()} sx={{ ...solidBtn, height: 42, px: 2.2 }}>
                    Add {noun} Expense
                </Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions, search, catFilter, categories, dueCount, hasReminders, payTiming, noun, inDetail, entry]);

    const amountLabel = trackStatus ? 'Price' : 'Amount';
    const textLabel = trackStatus ? 'Description' : 'Notes';

    // The dialog's fields in order. Half-width fields pair up; one left alone on a row (before a
    // full-width field, or last) is stretched to the full width so the form never has gaps.
    const formFields = [
        { key: 'date', node: <TextField label="Date" type="date" fullWidth size="small" value={form.date} onChange={set('date')} sx={field}
            // Clicking anywhere in the box (the dd-mm-yyyy text too), not only the calendar icon, opens the picker.
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { onClick: (e) => { try { e.currentTarget.showPicker(); } catch { /* needs a user gesture; focus is enough */ } }, style: { cursor: 'pointer' } } }} /> },
        ...(shootTypes ? [{ key: 'shootType', node: (
            <TextField select label="Shoot type" fullWidth size="small" value={form.shootType} onChange={set('shootType')} sx={field}>
                {shootTypes.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
        ) }] : []),
        { key: 'category', node: (
            <TextField
                select label="Category" fullWidth size="small" sx={field} value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            >
                {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                <MenuItem value={OTHERS}>Others…</MenuItem>
            </TextField>
        ) },
        ...(form.category === OTHERS ? [{ key: 'newCategory', full: true, node: (
            <TextField
                autoFocus fullWidth size="small" sx={field} label="New category"
                helperText="Type a name and press Enter to add it to the categories"
                value={customCat} onChange={(e) => setCustomCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!e.repeat) addCustomCategory(); } }}
            />
        ) }] : []),
        // Whichever category is selected also shows in this box, with a pencil to rename it.
        ...(form.category !== OTHERS ? [{ key: 'addedCategory', full: true, node: (
            <TextField
                fullWidth size="small" sx={field} label="Selected category"
                value={editingCat !== null ? editValue : form.category} error={editError}
                helperText={editError ? 'Name is empty or already used' : editingCat !== null ? 'Press Enter to save, Esc to cancel' : 'Click the pencil to rename it'}
                autoFocus={editingCat !== null}
                onChange={(e) => { setEditValue(e.target.value); setEditError(false); }}
                onKeyDown={(e) => {
                    if (editingCat === null) return;
                    if (e.key === 'Enter') { e.preventDefault(); if (!e.repeat) renameCategory(); }
                    if (e.key === 'Escape') { e.stopPropagation(); setEditingCat(null); }
                }}
                slotProps={{
                    input: {
                        readOnly: editingCat === null,
                        endAdornment: editingCat === null && (
                            <InputAdornment position="end">
                                <Tooltip title="Rename this category">
                                    <IconButton size="small" edge="end" onClick={() => { setEditingCat(form.category); setEditValue(form.category); setEditError(false); }}>
                                        <EditRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        ) }] : []),
        ...extraFields.map((f) => {
            if (!f.choices) {
                return { key: f.key, node: <TextField label={f.label} required={f.required} fullWidth size="small" value={form[f.key]} onChange={set(f.key)} sx={field} /> };
            }
            // Earlier shoot names (from expenses, and advances typed before any expense) are picked; Others… types a new one.
            const options = [...new Set(rows.map((r) => String(r[f.key] || '').trim()).filter(Boolean))].sort();
            const typing = customKeys[f.key] || options.length === 0;
            return {
                key: f.key,
                node: typing ? (
                    <TextField
                        label={f.label} required={f.required} fullWidth size="small" sx={field} value={form[f.key]} onChange={set(f.key)}
                        autoFocus={Boolean(customKeys[f.key])}
                        slotProps={options.length ? { input: { endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Pick an existing one">
                                    <IconButton size="small" edge="end" onClick={() => { setCustomKeys((p) => ({ ...p, [f.key]: false })); setForm((p) => ({ ...p, [f.key]: '' })); }}>
                                        <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) } } : undefined}
                    />
                ) : (
                    <TextField
                        select label={f.label} required={f.required} fullWidth size="small" sx={field} value={form[f.key]}
                        onChange={(e) => {
                            if (e.target.value === OTHERS) { setCustomKeys((p) => ({ ...p, [f.key]: true })); setForm((p) => ({ ...p, [f.key]: '' })); } else set(f.key)(e);
                        }}
                    >
                        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                        <MenuItem value={OTHERS}>Others…</MenuItem>
                    </TextField>
                ),
            };
        }),
        withEmployees
            ? { key: 'empRows', full: true, node: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>Employees &amp; amounts</Typography>
                    {empRows.map((row, i) => {
                        const taken = new Set(empRows.filter((_, j) => j !== i).map((r) => r.code));
                        const dup = row.code && taken.has(row.code);
                        const update = (patch) => setForm((p) => ({ ...p, empRows: p.empRows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
                        return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                <Autocomplete
                                    size="small" sx={{ flex: 1, minWidth: 0 }} autoHighlight
                                    options={employeeOptions.filter((o) => !taken.has(o.code))}
                                    getOptionLabel={(o) => o.name}
                                    isOptionEqualToValue={(a, b) => a.code === b.code}
                                    value={employeeOptions.find((o) => o.code === row.code) || null}
                                    onChange={(_, v) => update({ code: v ? v.code : '' })}
                                    noOptionsText="No employees found"
                                    renderOption={(props, o) => (<li {...props} key={o.code}><Box><Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.name}</Typography><Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{o.code}</Typography></Box></li>)}
                                    renderInput={(params) => <TextField {...params} label={`Employee ${i + 1}`} placeholder="Select employee" error={Boolean(dup)} slotProps={{ ...params.slotProps, inputLabel: { ...params.slotProps?.inputLabel, shrink: true } }} sx={field} />}
                                />
                                <TextField
                                    label="Amount (₹)" type="number" size="small" value={row.amount}
                                    onChange={(e) => update({ amount: e.target.value })}
                                    sx={{ ...field, width: 150, flexShrink: 0 }} slotProps={{ htmlInput: { min: 0 }, inputLabel: { shrink: true } }}
                                />
                                <Tooltip arrow title="Remove">
                                    <span>
                                        <IconButton size="small" disabled={empRows.length === 1 && !row.code && !row.amount}
                                            onClick={() => setForm((p) => ({ ...p, empRows: p.empRows.length > 1 ? p.empRows.filter((_, j) => j !== i) : [{ code: '', amount: '' }] }))}
                                            sx={{ color: '#98A0AE', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 19 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Button startIcon={<AddRoundedIcon />} onClick={() => setForm((p) => ({ ...p, empRows: [...p.empRows, { code: '', amount: '' }] }))}
                            sx={{ ...ghostBtn, height: 36, px: 1.6, fontSize: 12.5, color: PRIMARY, borderColor: '#C9BEFB', borderStyle: 'dashed' }}>Add employee</Button>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>
                            Total: {inr(filledRows.reduce((t, r) => t + (Number(r.amount) || 0), 0))}
                        </Typography>
                    </Box>
                </Box>
            ) }
                : { key: 'amount', node: <TextField label={trackStatus ? 'Price (₹)' : 'Amount (₹)'} type="number" fullWidth size="small" value={form.amount} onChange={set('amount')} sx={field} slotProps={{ htmlInput: { min: 0 } }} /> },
        { key: 'paymentMode', node: (
            <TextField select label="Payment mode" fullWidth size="small" value={form.paymentMode} onChange={set('paymentMode')} sx={field}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
        ) },
        ...(trackStatus ? [{ key: 'status', node: (
            <TextField select label="Status" fullWidth size="small" value={form.status} onChange={set('status')} sx={field}>
                <MenuItem value="Paid">Paid</MenuItem>
                {/* The server's value is "Partially Paid"; it is shown as "Partially". */}
                <MenuItem value="Partially Paid">Partially</MenuItem>
                <MenuItem value="Unpaid">Unpaid</MenuItem>
            </TextField>
        ) }] : []),
        { key: 'notes', full: true, node: <TextField label={textLabel} fullWidth size="small" multiline minRows={2} value={form.notes} onChange={set('notes')} sx={field} /> },
    ];
    const formSizes = (() => {
        const sizes = [];
        let half = false; // a half-width field is waiting for a partner
        formFields.forEach((f, i) => {
            if (f.full) {
                if (half) sizes[i - 1] = 12;
                sizes[i] = 12;
                half = false;
            } else {
                sizes[i] = 6;
                half = !half;
            }
        });
        if (half) sizes[sizes.length - 1] = 12;
        return sizes;
    })();


    // One entry per table column keeps the header, rows and grid template in step.
    const linkSx = { border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: PRIMARY, textAlign: 'left', '&:hover': { textDecoration: 'underline' } };
    const text = (fn, extra) => (r) => <Typography sx={{ fontSize: 13, ...extra }} noWrap>{fn(r) || '—'}</Typography>;
    const columns = [
        { label: 'DATE', w: '110px', render: text((r) => fmtDate(r.date)) },
        {
            label: 'CATEGORY', w: '1.1fr',
            render: text((r) => r.category, { fontWeight: 700 }),
        },
        ...(shootTypes ? [{ label: 'TYPE', w: '90px', render: text((r) => r.shootType) }] : []),
        ...extraFields.map((f) => (withEmployees && f.key === 'project'
            // On Shoot the first column is the shoot's name, with its note tucked underneath instead of a column of its own.
            ? { label: 'SHOOT NAME', w: '1.4fr', render: (r) => (
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{r.project || '—'}</Typography>
                    {r.notes && <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap title={r.notes}>{r.notes}</Typography>}
                </Box>
            ) }
            : { label: f.label.toUpperCase(), w: '1fr', render: text((r) => r[f.key]) })),
        ...(withEmployees ? [{
            label: 'EMPLOYEES', w: '1.5fr',
            render: (r) => ((r.employees || []).length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 0.4 }}>
                    {r.employees.map((n, i) => {
                        const amt = r.empStats?.[i]?.amount; // each person's own share
                        return (
                            <Box key={`${n}-${i}`} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap title={n}>{n}</Typography>
                                {amt > 0 && <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#2F8F5F', flexShrink: 0 }}>{inr(amt)}</Typography>}
                            </Box>
                        );
                    })}
                </Box>
            ) : <Typography sx={{ fontSize: 13 }}>—</Typography>),
        }] : []),
        { label: amountLabel.toUpperCase(), w: '110px', render: text((r) => inr(r.amount), { fontWeight: 800 }) },
        ...(withEmployees ? [] : [{ label: textLabel.toUpperCase(), w: '1.2fr', render: text((r) => r.notes, { color: '#64748B' }) }]),
        { label: 'PAID VIA', w: '110px', render: text((r) => r.paymentMode) },
        ...(trackStatus ? [{
            label: 'STATUS', w: '110px',
            render: (r) => (
                <Tooltip title="Click to toggle">
                    <Chip
                        size="small" label={r.status === 'Partially Paid' ? 'Partially' : r.status} onClick={(e) => { e.stopPropagation(); toggleStatus(r); }}
                        sx={r.status === 'Paid'
                            ? { fontWeight: 700, bgcolor: '#E8F7EF', color: '#2F8F5F' }
                            : r.status === 'Partially Paid'
                                ? { fontWeight: 700, bgcolor: '#FFF4E0', color: '#B7791F' }
                                : { fontWeight: 700, bgcolor: '#FDEBEE', color: '#C2475A' }}
                    />
                </Tooltip>
            ),
        }] : []),
        {
            label: '', w: '48px', pad: 0.5,
            render: (r) => <Tooltip title="Delete"><IconButton size="small" onClick={(e) => { e.stopPropagation(); remove(r.id); }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>,
        },
    ];
    const cols = columns.map((c) => c.w).join(' ');
    const statSize = { xs: 12, sm: 6, md: hasReminders ? 3 : 4 };

    if (entry) {
        return (
            <ExpenseProfile
                config={config} entry={entry} rows={rows}
                onOpenEntry={openEntry} onOpenEmployee={(code) => openDetail('employee', code)}
                onBack={goBack} onToggleStatus={toggleStatus}
                onDelete={async (id) => { if (await remove(id)) closeDetail(); }}
            />
        );
    }
    if (detail) {
        const empName_ = detail.kind === 'employee'
            ? (employeeOptions.find((o) => o.code === detail.value)?.name || rows.flatMap((r) => r.empStats || []).find((e) => e.code === detail.value)?.name || detail.value)
            : undefined;
        return (
            <ExpenseDetail
                config={config} kind={detail.kind} value={detail.value} title={empName_} rows={rows}
                columns={columns.slice(0, -1)} onOpenEntry={openEntry} onRemovePayment={data.removePayment} onRemoveExpense={data.removeExpense} refreshKey={rows.length + payments.length}
            />
        );
    }

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {error && <Alert severity="error" action={<Button size="small" onClick={data.reload}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}
            {hasReminders && <DueReminders reminders={activeReminders} onDismiss={dismissReminder} />}

            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid size={statSize}><AttStatCard label="Total" value={inr(total)} sub={`${rows.length} entries`} icon={PaymentsRoundedIcon} tone="purple" /></Grid>
                <Grid size={statSize}><AttStatCard label="This Month" value={inr(monthTotal)} icon={CalendarMonthRoundedIcon} tone="blue" /></Grid>
                <Grid size={statSize}><AttStatCard label="Today" value={inr(todayTotal)} icon={TodayRoundedIcon} tone="green" /></Grid>
                {hasReminders && (
                    <Grid size={statSize}>
                        <AttStatCard label="Reminders" value={dueCount} sub={`${reminders.length} saved`} icon={NotificationsActiveRoundedIcon} tone="amber" />
                    </Grid>
                )}
            </Grid>

            {shootTypes && (
                <ToggleButtonGroup
                    exclusive size="small" value={typeFilter} onChange={(_, v) => v && setTypeFilter(v)}
                    sx={{ mb: 2.5, '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2, borderRadius: '7px !important', mr: 1, border: '1px solid #E5E7EB !important', bgcolor: '#fff' }, '& .Mui-selected': { bgcolor: '#EFECFE !important', color: '#6B58D6' } }}
                >
                    <ToggleButton value="all">All ({rows.length})</ToggleButton>
                    {shootTypes.map((s) => (
                        <ToggleButton key={s} value={s}>{s} ({rows.filter((r) => r.shootType === s).length})</ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}

            <Box sx={{ ...card, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 760 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: cols, bgcolor: '#F7F6FD' }}>
                            {columns.map((c, i) => <Box key={`${c.label}${i}`} sx={th}>{c.label}</Box>)}
                        </Box>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                        ) : visible.length === 0 ? (
                            <EmptyState icon={ReceiptLongRoundedIcon} title={`No ${noun.toLowerCase()} expenses yet`} hint={`Use “Add ${noun} Expense” to record one.`} />
                        ) : visible.map((r) => (
                            <Box key={r.id} onClick={() => openEntry(r.id)} sx={{ display: 'grid', gridTemplateColumns: cols, cursor: 'pointer', borderBottom: '1px solid #EEF0F6', '&:last-of-type': { borderBottom: 'none' }, '&:hover': { bgcolor: '#FAFAFE' } }}>
                                {columns.map((c, i) => <Box key={i} sx={{ ...td, borderBottom: 'none', display: 'flex', alignItems: 'center', ...(c.pad !== undefined ? { px: c.pad } : {}), minWidth: 0 }}>{c.render(r)}</Box>)}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: DIALOG_PAPER }}>
                <DialogTitle sx={dialogTitleSx}>Add {noun} Expense</DialogTitle>
                <DialogContent sx={dialogBodySx}>
                    <Grid container spacing={2.5}>
                        {formFields.map((f, i) => (
                            <Grid key={f.key} size={{ xs: 12, sm: formSizes[i] }}>{f.node}</Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={() => setOpen(false)} disabled={saving} sx={ghostBtn}>Cancel</Button>
                    <Button onClick={save} disabled={!canSave || saving} sx={solidBtn}>{saving ? 'Saving…' : 'Save'}</Button>
                </DialogActions>
            </Dialog>

            {payTiming && (
                <PaymentDialog
                    open={payOpen} onClose={() => setPayOpen(false)} employees={employeeOptions}
                    loadShoots={data.loadShootNames}
                    onSave={async (p) => { await data.addPayment(p); notify('Payment added'); }}
                />
            )}

            {hasReminders && (
                <ReminderDialog
                    open={remOpen} onClose={() => setRemOpen(false)} categories={categories.filter((c) => categoryIds[c] != null)} reminders={reminders}
                    onAdd={async (r) => { await data.addReminder(r); notify('Reminder set'); }}
                    onDismiss={dismissReminder}
                />
            )}

            <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
                <Alert severity={snack?.sev || 'success'} variant="filled" onClose={() => setSnack(null)}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
